import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import BottomNav from '../../components/common/BottomNav';
import Sidebar from '../../components/common/Sidebar';
import DoctorCard from '../../components/common/DoctorCard';
import StarRating from '../../components/common/StarRating';
import { InlineLoader } from '../../components/common/Loader';
import NotificationsPanel from '../../components/common/NotificationsPanel';
import { searchDoctors, getLatestReviews, getAppReviews } from '../../services/patientService';
import { DISEASES, CHECKUP_TYPES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import { usePatientNotifications } from '../../hooks/usePatientNotifications';
import { useSocket } from '../../context/SocketContext';

const DISEASE_COLORS = [
  { bg:'bg-red-50',    color:'text-red-500' },
  { bg:'bg-purple-50', color:'text-purple-500' },
  { bg:'bg-blue-50',   color:'text-blue-500' },
  { bg:'bg-orange-50', color:'text-orange-500' },
  { bg:'bg-green-50',  color:'text-green-500' },
  { bg:'bg-pink-50',   color:'text-pink-500' },
  { bg:'bg-amber-50',  color:'text-amber-600' },
  { bg:'bg-cyan-50',   color:'text-cyan-600' },
];

function DiseaseChip({ icon, label, onClick, bg, color }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[72px]">
      <div className={`w-14 h-14 rounded-2xl ${bg} flex items-center justify-center shadow-sm`}>
        <i className={`fa-solid ${icon} ${color} text-xl`} />
      </div>
      <span className="text-[10px] text-text-secondary font-medium text-center leading-tight">{label}</span>
    </button>
  );
}

function CheckupChip({ icon, label, onClick }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-border bg-white flex-shrink-0 active:bg-primary-light shadow-sm">
      <i className={`fa-solid ${icon} text-primary text-sm`} />
      <span className="text-xs text-text-primary font-medium whitespace-nowrap">{label}</span>
    </button>
  );
}

function ClinicCard({ doctor }) {
  const navigate = useNavigate();
  const img = doctor.clinicImages?.[0] || doctor.profileImg || null;
  return (
    <button onClick={() => navigate(`/doctor/${doctor._id}`)}
      className="flex-shrink-0 w-44 rounded-2xl overflow-hidden shadow-card border border-border bg-white active:opacity-80 text-left">
      <div className="w-full h-36 bg-gradient-to-br from-primary-light to-blue-50 flex items-center justify-center overflow-hidden relative">
        {img ? (
          <>
            <img src={img} alt={doctor.clinicName} className="w-full h-full object-cover"
              onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
            <div className="hidden w-full h-full items-center justify-center">
              <i className="fa-solid fa-hospital text-primary text-4xl" />
            </div>
          </>
        ) : <i className="fa-solid fa-hospital text-primary text-4xl" />}
        {doctor.isVerified && (
          <div className="absolute top-2 right-2 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded-full">✓ Verified</div>
        )}
      </div>
      <div className="p-2.5">
        <p className="text-xs font-bold text-text-primary truncate">{doctor.clinicName}</p>
        <p className="text-[10px] text-primary font-medium truncate mt-0.5">{doctor.speciality || 'General'}</p>
        <div className="flex items-center justify-between mt-1.5">
          <StarRating rating={doctor.rating || 0} showCount={false} />
          {doctor.consultationFee > 0 && <span className="text-[9px] text-text-muted">₹{doctor.consultationFee}</span>}
        </div>
        {doctor.location?.city && (
          <p className="text-[9px] text-text-muted mt-1 flex items-center gap-0.5 truncate">
            <i className="fa-solid fa-location-dot text-primary" />{doctor.location.city}
          </p>
        )}
      </div>
    </button>
  );
}

function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = React.useState(0);
  const ref = React.useRef(null);
  const started = React.useRef(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const num = parseInt(target.replace(/\D/g, ''));
        const duration = 1800;
        const steps = 40;
        const increment = num / steps;
        let current = 0;
        const timer = setInterval(() => {
          current += increment;
          if (current >= num) { setCount(num); clearInterval(timer); }
          else setCount(Math.floor(current));
        }, duration / steps);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count}{suffix}</span>;
}


// ── Founders Section ───────────────────────────────────────────────────────
function FoundersSection() {
  const founders = [
    {
      name:    'Roj Mohammad',
      role:    'CEO & Founder',
      company: 'Faristaa',
      bio:     'Leading the vision to make healthcare more accessible, efficient, and patient-centric through innovative technology solutions. Focused on building a trusted healthcare ecosystem that connects patients, doctors, and healthcare providers seamlessly.',
      side:    'left',   // which half of the image
    },
    {
      name:    'Seraj Ansari',
      role:    'Co-Founder',
      company: 'Faristaa',
      bio:     'Playing a key role in strategy, operations, and business growth. Working closely on developing scalable solutions, strengthening partnerships, and ensuring the platform delivers a smooth and impactful healthcare experience for all users.',
      side:    'right',
    },
  ];

  return (
    <section className="mt-10 mb-2 px-4">
      {/* Section header */}
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-1.5 bg-primary-light text-primary text-xs font-bold px-4 py-1.5 rounded-full mb-3">
          <i className="fa-solid fa-users text-[11px]" /> Meet the Founders
        </span>
        <h2 className="text-2xl font-black text-text-primary font-heading leading-tight">
          The People Behind <span className="text-primary">Faristaa</span>
        </h2>
        <p className="text-sm text-text-secondary mt-2 max-w-xs mx-auto leading-relaxed">
          Passionate about transforming healthcare in India — one appointment at a time.
        </p>
      </div>

      {/* Shared founders photo */}
      <div className="relative mb-8 rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: 'linear-gradient(135deg,#0D9488 0%,#134E4A 100%)' }}>
        {/* Decorative top accent */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-teal-300 to-primary opacity-80" />

        <img
          src="/founders.png"
          alt="Roj Mohammad and Seraj Ansari — Founders of Faristaa"
          className="w-full object-cover object-top"
          style={{ maxHeight: 320 }}
          onError={e => { e.target.style.display='none'; }}
        />

        {/* Gradient overlay bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-24"
          style={{ background: 'linear-gradient(to top, rgba(13,148,136,0.85), transparent)' }} />

        {/* Name tags floating on image — CEO tag on right, Co-Founder tag on left,
            as requested (image itself unchanged — only label positions swapped) */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-between px-5">
          <div className="flex flex-col items-start">
            <span className="text-white text-[10px] font-semibold bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded-full mb-1 flex items-center">
              <i className="fa-solid fa-handshake text-amber-400 text-[9px] mr-1" />Co-Founder
            </span>
            <p className="text-white font-black text-sm leading-tight drop-shadow-md">Seraj Ansari</p>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-white text-[10px] font-semibold bg-black/30 backdrop-blur-sm px-2 py-0.5 rounded-full mb-1 flex items-center">
              <i className="fa-solid fa-crown text-amber-400 text-[9px] mr-1" />CEO & Founder
            </span>
            <p className="text-white font-black text-sm leading-tight drop-shadow-md">Roj Mohammad</p>
          </div>
        </div>
      </div>

      {/* Individual founder cards */}
      <div className="space-y-4">
        {founders.map((f, i) => (
          <div key={i}
            className="relative bg-white rounded-2xl border border-border overflow-hidden shadow-card"
            style={{ boxShadow: '0 2px 12px rgba(13,148,136,0.08)' }}>

            {/* Left accent bar */}
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl"
              style={{ background: 'linear-gradient(180deg,#0D9488,#0F766E)' }} />

            <div className="pl-5 pr-4 py-4">
              {/* Name + role */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-base font-black text-text-primary font-heading leading-tight">{f.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[11px] font-bold text-primary">{f.role}</span>
                    <span className="text-text-muted text-[10px]">·</span>
                    <span className="text-[11px] text-text-muted font-medium">{f.company}</span>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ml-2"
                  style={{ background: 'linear-gradient(135deg,#E0F2F1,#B2DFDB)' }}>
                  <i className={`fa-solid ${i === 0 ? 'fa-crown' : 'fa-handshake'} text-primary text-base`} />
                </div>
              </div>

              {/* Bio */}
              <p className="text-xs text-text-secondary leading-relaxed">{f.bio}</p>

              {/* Pills */}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(i === 0
                  ? ['Healthcare Tech', 'Vision & Strategy', 'Product Leadership']
                  : ['Operations', 'Business Growth', 'Partnerships']
                ).map(tag => (
                  <span key={tag}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background:'#E0F2F1', color:'#0D9488' }}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Company tagline footer */}
      <div className="mt-6 text-center py-4 rounded-2xl"
        style={{ background:'linear-gradient(135deg,#F0FDFA,#E0F2F1)' }}>
        <i className="fa-solid fa-heart-pulse text-primary text-lg mb-2 block" />
        <p className="text-xs font-bold text-primary">Built with passion for Indian healthcare</p>
        <p className="text-[10px] text-text-muted mt-0.5">Faristaa — Connecting patients & doctors across India</p>
      </div>
    </section>
  );
}

function AboutUs() {
  const cards = [
    { icon:'fa-user-doctor',    color:'bg-teal-50',    iconColor:'text-primary',     title:'Expert Verified Doctors',  desc:'Every doctor on FARISTAA is manually verified by our admin team before they can accept bookings.' },
    { icon:'fa-calendar-check', color:'bg-blue-50',    iconColor:'text-blue-600',    title:'Instant Slot Booking',     desc:'Book your slot in seconds, get a token number and QR code, and walk in at your scheduled time.' },
    { icon:'fa-video',          color:'bg-purple-50',  iconColor:'text-purple-600',  title:'Video Consultation',       desc:'Secure, encrypted video calls with your doctor from the comfort of your home.' },
    { icon:'fa-qrcode',         color:'bg-amber-50',   iconColor:'text-amber-600',   title:'QR Code Check-in',         desc:'Skip paperwork at the clinic. Just show your QR code and get checked in instantly.' },
    { icon:'fa-pills',          color:'bg-green-50',   iconColor:'text-green-600',   title:'Medicine Store Chat',      desc:'Order medicines and get prescription help directly through the app chat feature.' },
    { icon:'fa-shield-halved',  color:'bg-red-50',     iconColor:'text-red-500',     title:'Secure Payments',          desc:'All payments are encrypted and processed via Razorpay. Your data is always protected.' },
  ];

  return (
    <section className="mt-8 mb-6">
      {/* Heading */}
      <div className="text-center mb-6 px-4">
        <span className="inline-block bg-primary-light text-primary text-xs font-semibold px-3 py-1 rounded-full mb-2">About FARISTAA</span>
        <h2 className="text-2xl font-bold text-text-primary font-heading leading-tight">
          Healthcare Made<br /><span className="text-primary">Simple &amp; Accessible</span>
        </h2>
        <p className="text-sm text-text-secondary mt-2 leading-relaxed max-w-sm mx-auto">
          Your trusted digital health companion — bridging the gap between patients and quality healthcare across India.
        </p>
      </div>

      {/* 6 cards — 1 col mobile, 2 col desktop */}
      <div className="px-4 grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {cards.map((card, i) => (
          <div key={i} className={`${card.color} rounded-2xl p-4 flex items-start gap-3 border border-white`}>
            <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
              <i className={`fa-solid ${card.icon} ${card.iconColor} text-lg`} />
            </div>
            <div>
              <p className="text-sm font-bold text-text-primary mb-1">{card.title}</p>
              <p className="text-xs text-text-secondary leading-relaxed">{card.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Animated counters */}
      <div className="mx-4 grid grid-cols-3 gap-3 mb-4">
        {[
          { target:'500+', suffix:'', label:'Verified Doctors',  num:'500', plus:true },
          { target:'10000+', suffix:'', label:'Happy Patients',  num:'10000', plus:true },
          { target:'50+', suffix:'', label:'Specialities',       num:'50', plus:true },
        ].map(s => (
          <div key={s.label} className="text-center bg-primary-light rounded-2xl py-4 px-2">
            <p className="text-xl font-black text-primary font-heading">
              <AnimatedCounter target={s.target} />{s.plus ? '+' : ''}
            </p>
            <p className="text-[10px] text-primary/80 font-semibold mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Mission banner */}
      <div className="mx-4 p-4 rounded-2xl text-white" style={{background:'linear-gradient(135deg,#0D9488,#0F766E)'}}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-bullseye text-white text-base" />
          </div>
          <div>
            <h4 className="font-bold font-heading mb-1">Our Mission</h4>
            <p className="text-sm text-white/90 leading-relaxed">
              To make quality healthcare accessible to every Indian — connecting patients with the right doctor at the right time, without barriers of distance or complexity.
            </p>
          </div>
        </div>
      </div>

      {/* ── Founders Section ── */}
      <FoundersSection />
    </section>
  );
}


// ── Legal Policy Modal ─────────────────────────────────────────────────────
const LEGAL_CONTENT = {
  terms: {
    title: 'Terms & Conditions',
    lastUpdated: 'May 11, 2026',
    icon: 'fa-file-contract',
    sections: [
      { heading: '1. About FARISTAA', body: 'Faristaa is operated by FARISTAA HEALTHTECH PRIVATE LIMITED, a company incorporated under the laws of India. Faristaa is a digital healthcare technology and communication platform designed to simplify healthcare accessibility, appointment management, healthcare communication, and digital healthcare coordination. FARISTAA HEALTHTECH PRIVATE LIMITED operates only as a digital technology and communication platform and does not directly provide medical diagnosis, medical treatment, prescription services, emergency healthcare services, or independent medical advice.' },
      { heading: '2. Acceptance of Terms', body: 'By accessing, registering, browsing, or using FARISTAA, all users including patients, doctors, clinics, hospitals, diagnostic centers, healthcare staff, receptionists, and associated healthcare professionals agree to comply with these Terms and Conditions. Continued use of the platform automatically indicates acceptance of all terms, conditions, and future modifications.' },
      { heading: '3. User Eligibility & Registration', body: 'Users accessing FARISTAA must be legally eligible to use digital healthcare services. All information submitted during registration must be accurate and updated. Parents or legal guardians may use the platform on behalf of minors. FARISTAA reserves the right to suspend or terminate access if false or fraudulent information is detected.' },
      { heading: '4. Doctor Appointment Booking', body: 'FARISTAA allows users to search and book appointments according to doctor specialization, consultation timing, and availability. Appointment services may include physical clinic visits, online video consultations, and diagnostic appointments. FARISTAA does not guarantee uninterrupted appointment availability or consultation continuity.' },
      { heading: '5. QR-Based Appointment System', body: 'FARISTAA provides personalized QR-based healthcare access systems. After successful booking, a secure QR Code is generated for appointment verification at clinics. Users agree not to duplicate QR systems fraudulently, manipulate appointment systems, or share QR access for unauthorized purposes.' },
      { heading: '6. Online Video Consultation', body: 'Doctors may independently provide online video consultation services. FARISTAA is not responsible for internet connectivity failures, consultation interruptions, or healthcare outcomes arising from consultations. Online video consultations should not be considered emergency healthcare services.' },
      { heading: '7. Payments & Platform Charges', body: 'Users may make digital payments through FARISTAA for appointments and consultations. FARISTAA applies platform service charges for technology infrastructure and operational management. Users agree to pay all applicable charges before appointment confirmation.' },
      { heading: '8. Doctor Earnings & Payouts', body: 'Doctors receive earnings for consultations conducted through FARISTAA. Earnings become eligible after successful consultation completion. FARISTAA reserves the right to verify transactions before payout and temporarily hold suspicious transactions.' },
      { heading: '9. Cancellation & Refund', body: 'Users may cancel appointments according to applicable cancellation rules and healthcare provider policies. Refund eligibility depends on time of cancellation, consultation status, and provider policies. Platform charges may be non-refundable in certain situations.' },
      { heading: '10. Intellectual Property', body: 'All FARISTAA platform content including logos, branding, software systems, QR technologies, platform designs, and digital infrastructure remain the intellectual property of FARISTAA. Unauthorized copying, modification, reproduction, or misuse is strictly prohibited.' },
      { heading: '11. Limitation of Liability', body: 'FARISTAA shall not be held responsible for medical decisions, prescription outcomes, healthcare complications, treatment effectiveness, independent doctor conduct, or consultation outcomes. Healthcare responsibility remains with independent healthcare providers and users.' },
      { heading: '12. Suspension or Termination', body: 'FARISTAA reserves the right to suspend, restrict, or terminate platform access for users involved in fraudulent activity, policy violations, misuse of healthcare systems, or unauthorized access. Such actions may be taken without prior notice where required for platform security.' },
      { heading: '13. Governing Law', body: 'These Terms and Conditions shall be governed according to the applicable laws of India. Any disputes shall be subject to applicable jurisdiction under Indian law.' },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'May 12, 2026',
    icon: 'fa-shield-halved',
    sections: [
      { heading: '1. About FARISTAA', body: 'Faristaa is operated by FARISTAA HEALTHTECH PRIVATE LIMITED, a company incorporated under the laws of India. FARISTAA HEALTHTECH PRIVATE LIMITED operates a digital healthcare technology and communication platform and not as a direct healthcare treatment provider. All healthcare consultations, prescriptions, and medical decisions are independently provided by doctors and clinics using the platform. For privacy-related queries, contact us at support@faristaa.in.' },
      { heading: '2. Information We Collect', body: 'FARISTAA may collect personal information including full name, mobile number, email address, gender, date of birth, address, profile photographs, and user identification details. Healthcare-related information such as consultation history, appointment records, prescription documents, and medical reports may also be collected when voluntarily provided.' },
      { heading: '3. Technical & Payment Information', body: 'FARISTAA may automatically collect technical information such as IP address, device identifiers, browser type, operating system, session information, and login activity. Payments may involve transaction IDs, payment status, and billing details. Complete banking credentials or card numbers are not directly stored by FARISTAA.' },
      { heading: '4. How Information Is Used', body: 'Information is used for appointment booking, QR-based verification, user authentication, video consultation coordination, healthcare communication, payment processing, fraud prevention, platform analytics, notification systems, and security monitoring.' },
      { heading: '5. Data Sharing & Disclosure', body: 'FARISTAA does not sell healthcare-related information to unauthorized third parties. Information may be shared with healthcare providers selected by users, payment gateways for transaction processing, cloud hosting providers, communication service providers, and government authorities where legally required.' },
      { heading: '6. Data Security', body: 'FARISTAA uses encryption technologies, secure servers, authentication protections, restricted administrative access, and security monitoring to protect user information. No internet-based platform can guarantee complete security. Users are responsible for maintaining account confidentiality and protecting passwords.' },
      { heading: '7. QR-Based Access Systems', body: 'FARISTAA provides QR-based healthcare access systems. When users scan QR Codes, the healthcare provider profile may open and appointment booking becomes available. Users agree not to duplicate QR systems fraudulently or misuse healthcare access technologies.' },
      { heading: '8. Data Retention & Deletion', body: 'Information may be retained for healthcare coordination, financial reporting, fraud prevention, security monitoring, and legal compliance. Users may request account deletion through official support channels. Certain information may be retained where legally required.' },
      { heading: '9. User Rights', body: 'Users may request access to their information, correction or updating of data, communication preference changes, withdrawal of certain consents, and account deletion support. FARISTAA may verify user identity before processing certain requests.' },
      { heading: '10. Governing Law', body: 'This Privacy Policy shall be governed according to the applicable laws of India. Any disputes related to privacy or platform usage shall be subject to applicable jurisdiction under Indian law.' },
    ],
  },
  refund: {
    title: 'Cancellation & Refund Policy',
    lastUpdated: 'May 2026',
    icon: 'fa-rotate-left',
    sections: [
      { heading: '1. Introduction', body: 'FARISTAA is a digital healthcare technology and communication platform designed to simplify appointment booking, QR-based healthcare access, online consultations, queue management, and healthcare communication between patients and independent healthcare providers.' },
      { heading: '2. OPD Consultation Fee & Platform Charge', body: 'Users booking appointments pay the OPD consultation fee decided by the doctor/clinic plus an additional FARISTAA platform service charge. The platform charge is strictly non-refundable under normal circumstances as it covers appointment infrastructure, QR systems, communication systems, technical operations, and platform maintenance.' },
      { heading: '3. Clinic Visit Reporting Requirement', body: 'Patients must visit the clinic and complete required check-in procedures at least 30 minutes before their booked slot timing. Late arrival may affect queue sequence, appointment validity, consultation timing, and refund eligibility.' },
      { heading: '4. Cancellation & Refund Rules', body: 'This policy is issued by FARISTAA HEALTHTECH PRIVATE LIMITED. When a patient cancels an appointment, the refund amount is processed according to the refund rules and cancellation policies decided by the respective doctor/clinic. The refundable amount may vary depending on doctor cancellation policy, appointment timing, and operational conditions. The FARISTAA platform service charge remains strictly non-refundable regardless of cancellation timing, missed appointments, or connectivity issues.' },
      { heading: '5. Video Consultation Rules', body: 'For online video consultations, users should remain available at least 20-30 minutes before their consultation timing, keep their device active, and remain in a proper internet/network area. This helps doctors communicate properly with patients.' },
      { heading: '6. Missed Doctor Call Conditions', body: 'If the doctor attempts to contact the patient during the booked slot and the patient does not receive the call, remains unavailable, or fails to respond, the consultation may be treated as completed from the provider side and doctor payment may become fully approved. The FARISTAA platform charge remains non-refundable.' },
      { heading: '7. Medicine Coordination Disclaimer', body: 'Patients may independently coordinate medicine-related communication with doctors, clinics, and medical staff. FARISTAA itself does not sell medicines, manufacture medicines, independently prescribe medicines, or directly participate in medicine transactions. Medicine coordination between patients and providers remains their independent responsibility.' },
      { heading: '8. Platform Role Limitation', body: 'FARISTAA operates only as a healthcare technology and communication platform and shall not be responsible for treatment outcomes, medicine effectiveness, consultation quality, prescription handling, independent doctor actions, or healthcare decisions made between patients and providers.' },
    ],
  },
  discount_terms: {
    title: 'Discount Offer Terms',
    lastUpdated: 'May 2026',
    icon: 'fa-tag',
    sections: [
      { heading: 'Doctor-Managed Discount Offers', body: 'These terms are issued by FARISTAA HEALTHTECH PRIVATE LIMITED. Doctors and clinics using Faristaa may independently create and manage discount offers through their dashboard settings. The doctor may decide the discount percentage, offer validity, consultation conditions, and applicable duration according to their own preferences.' },
      { heading: 'Automatic Discount Application', body: 'If a patient/user books an appointment within the active discount period set by the doctor, the applicable discount may automatically apply during booking. The system checks the patient\'s last visit date against the doctor\'s configured validity window.' },
      { heading: 'Discount Validity & Expiry', body: 'The discount shall only remain valid within the duration, timing, and conditions configured by the respective doctor/clinic. If the patient books, visits, or attempts to use the offer after the expiry date or beyond the applicable validity period, the discount may no longer apply. In such situations, the normal consultation fee may become payable.' },
      { heading: 'Online & Offline Visits', body: 'Returning patient discounts apply to both online bookings (clinic/video) and walk-in offline visits. The system tracks visit history across both channels to determine eligibility. If the validity window has passed since the last visit — whether online or offline — the discount will not apply.' },
      { heading: 'FARISTAA\'s Role', body: 'FARISTAA does not independently control, modify, or guarantee doctor discount offers or promotional pricing. All discount-related conditions shall remain subject to the respective doctor/provider settings available at the time of booking.' },
      { heading: 'User Agreement', body: 'By continuing to use FARISTAA, users acknowledge and agree to these discount-related operational conditions. FARISTAA acts only as a technology platform facilitating the discount system configured by healthcare providers.' },
    ],
  },
  about: {
    title: 'About FARISTAA',
    lastUpdated: 'May 2026',
    icon: 'fa-circle-info',
    sections: [
      { heading: 'What is FARISTAA?', body: 'FARISTAA is a modern and innovative HealthTech platform designed to digitally connect patients, doctors, clinics, diagnostic centers, radiology services, and healthcare staff within one smart healthcare ecosystem. The platform aims to simplify healthcare services by making them faster, smarter, more accessible, and technology-driven for everyone.' },
      { heading: 'Smart Appointment Booking', body: 'FARISTAA provides an advanced doctor appointment booking system where patients can easily search doctors according to location, specialty, clinic timing, and availability. Users can book appointments online, choose suitable time slots, make secure digital payments, and receive instant appointment confirmation without standing in long queues.' },
      { heading: 'Smart QR-Based Doctor Access', body: 'One of the key innovations of FARISTAA is its Smart QR-Based Doctor Access System. Doctors can use personalized QR codes on prescription papers, files, visiting cards, clinic posters, and other medical documents. By simply scanning the QR code, patients are directly redirected to the doctor\'s FARISTAA profile where they can instantly view doctor details and book appointments online anytime.' },
      { heading: 'QR-Based Appointment Verification', body: 'After booking an appointment, patients receive a unique QR code that can be scanned at the reception for instant appointment verification, faster check-in, reduced paperwork, and real-time patient updates for doctors and staff.' },
      { heading: 'Online Video Consultations', body: 'FARISTAA supports secure online video consultations that allow patients to consult doctors remotely from home or any location. This feature is highly beneficial for follow-up consultations, elderly patients, emergency guidance, busy professionals, and people living far from healthcare facilities.' },
      { heading: 'Healthcare Communication', body: 'FARISTAA provides direct communication features where patients can interact with medical staff through chat and audio calls regarding medicines, dosage instructions, reports, prescriptions, and follow-up guidance.' },
      { heading: 'Earnings & Payments for Doctors', body: 'For doctors and healthcare providers, FARISTAA offers a secure and transparent payment and earnings management system with digital payment processing, flexible payout options, real-time transaction records, and appointment-wise earning management.' },
      { heading: 'Real-Time Appointment Tracking', body: 'FARISTAA improves patient convenience through real-time appointment tracking, allowing users to check doctor availability, queue status, delay updates, and estimated waiting times before visiting clinics.' },
      { heading: 'Our Vision', body: 'Built with a strong focus on security, innovation, convenience, and digital healthcare management, FARISTAA aims to create a connected, transparent, and technology-driven healthcare ecosystem across India. The vision of FARISTAA is to modernize healthcare services, reduce waiting times, improve accessibility, and make quality healthcare easier and smarter for everyone.' },
    ],
  },
  cookie: {
    title: 'Cookie Policy',
    lastUpdated: 'May 22, 2026',
    icon: 'fa-cookie-bite',
    sections: [
      { heading: '1. Introduction', body: 'This Cookies Policy is issued by FARISTAA HEALTHTECH PRIVATE LIMITED and explains how Faristaa uses cookies, tracking technologies, session systems, and related technical tools while users access Faristaa websites, applications, QR systems, healthcare communication systems, appointment systems, and related platform services. By continuing to use Faristaa, users acknowledge they have read, understood, and agreed to the use of cookies as described.' },
      { heading: '2. What Are Cookies', body: 'Cookies are small text files or technical identifiers stored on a user\'s browser or device when visiting websites or using digital platforms. Cookies help FARISTAA improve platform functionality, user authentication, session management, appointment systems, login continuity, technical performance, security monitoring, and user experience.' },
      { heading: '3. Essential Cookies', body: 'Essential cookies help basic platform operations such as user login systems, account authentication, session maintenance, appointment booking systems, QR-based healthcare access, and platform security functions. Without essential cookies, some FARISTAA services may not function properly.' },
      { heading: '4. Performance & Analytics Cookies', body: 'These cookies help FARISTAA understand user interaction patterns, technical performance, error reports, platform usage statistics, traffic behavior, and system optimization requirements. This information helps improve platform efficiency and healthcare technology operations.' },
      { heading: '5. Security & Preference Cookies', body: 'Security cookies help prevent unauthorized access, detect suspicious activity, reduce fraud risks, and maintain platform integrity. Preference cookies remember language preferences, login preferences, device settings, and session continuity to improve user convenience.' },
      { heading: '6. Information Collected Through Cookies', body: 'Cookies may collect limited technical information including IP address, browser type, device information, session activity, login status, and technical identifiers. Cookies generally do not directly collect medical diagnosis information unless required for healthcare coordination systems.' },
      { heading: '7. Third-Party Cookies', body: 'FARISTAA may use third-party services that independently place cookies including cloud hosting providers, analytics providers, payment gateways, communication service providers, and video consultation systems. Such third-party systems operate according to their own policies. FARISTAA is not directly responsible for independent third-party cookie practices.' },
      { heading: '8. Cookie Management & User Control', body: 'Users may control or disable cookies through browser or device settings. However, restricting certain cookies may affect login functionality, appointment systems, QR access, platform security, and session continuity. Some FARISTAA services may become partially unavailable if essential cookies are disabled.' },
      { heading: '9. Data Security', body: 'FARISTAA uses reasonable technical and organizational measures to protect cookie-related technical information. No internet-based system can guarantee complete security. Users are encouraged to use secure devices, protect login credentials, and use trusted internet connections.' },
      { heading: '10. User Agreement', body: 'By continuing to access or use FARISTAA, users acknowledge they have read, understood, and agreed to this Cookies Policy. If any user does not agree, they should discontinue platform usage and disable cookies through browser settings.' },
    ],
  },
};

function LegalModal({ type, onClose }) {
  const data = LEGAL_CONTENT[type];
  if (!data) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl max-h-[88vh] flex flex-col md:inset-x-auto md:inset-y-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[560px] md:max-h-[80vh] md:rounded-3xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <div className="w-9 h-9 bg-primary-light rounded-xl flex items-center justify-center">
            <i className={`fa-solid ${data.icon} text-primary text-sm`} />
          </div>
          <h2 className="text-base font-bold text-text-primary flex-1 font-heading">{data.title}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-surface flex items-center justify-center">
            <i className="fa-solid fa-xmark text-text-secondary" />
          </button>
        </div>
        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {data.sections.map((s, i) => (
            <div key={i}>
              <p className="text-sm font-bold text-text-primary mb-1">{s.heading}</p>
              <p className="text-xs text-text-secondary leading-relaxed">{s.body}</p>
            </div>
          ))}
          <p className="text-[10px] text-text-muted text-center pt-2">Last Updated: {data.lastUpdated || 'May 2026'} · FARISTAA Platform</p>
        </div>
        {/* Close button */}
        <div className="px-5 py-4 border-t border-border flex-shrink-0">
          <button onClick={onClose} className="w-full py-3 bg-primary text-white text-sm font-bold rounded-2xl">
            Close
          </button>
        </div>
      </div>
    </>
  );
}


// ── Static fallback reviews shown when no real reviews exist yet ──────────
// ── FAQ Section ───────────────────────────────────────────────────────────
const FAQS = [
  { q: 'What is Faristaa?', a: 'Faristaa is a smart doctor appointment and healthcare platform that helps patients book appointments, manage tokens, access digital prescriptions, and connect with clinics easily through a modern web application.' },
  { q: 'Is my medical and personal data secure?', a: 'Yes. Faristaa uses secure authentication, encrypted connections (HTTPS), protected APIs, and role-based access systems to keep patient and clinic data safe and private.' },
  { q: 'Can I book appointments online instantly?', a: 'Yes. Patients can search doctors, select available slots, and book appointments instantly through the platform without visiting the clinic physically.' },
  { q: 'Does Faristaa support QR-based check-in?', a: 'Yes. Faristaa provides a smart QR-based check-in system. Patients receive a QR code after booking, which can be scanned at the clinic using mobile cameras or physical QR scanners.' },
  { q: 'Can clinics use Faristaa on mobile devices?', a: 'Yes. Faristaa is fully mobile responsive and supports PWA installation, allowing clinics and patients to use it like a real app on phones, tablets, laptops, and desktops.' },
  { q: 'What happens if internet connectivity becomes weak?', a: 'Faristaa is designed with smart network handling and offline-friendly features. Some data remains accessible temporarily, and the system automatically reconnects when internet is restored.' },
  { q: 'Does Faristaa support online payments?', a: 'Yes. Faristaa supports secure online payment integration through trusted gateways like Razorpay for appointment and service payments.' },
  { q: 'Can multiple staff members manage appointments together?', a: 'Yes. Clinics can provide controlled access to doctors, receptionists, and staff with role-based permissions for managing appointments, check-ins, and patient workflows.' },
  { q: 'Will patients receive appointment updates and notifications?', a: 'Yes. Patients receive important updates such as appointment confirmations, token status, reminders, and clinic notifications through integrated communication services.' },
  { q: 'Is there a returning patient discount?', a: 'Yes. Doctors can configure a discount for returning patients — for both online and walk-in bookings. The system automatically detects previous visits and applies the discount.' },
];

function FAQSection() {
  const [openIdx, setOpenIdx] = React.useState(null);
  return (
    <div className="mx-4 md:mx-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-primary-light rounded-xl flex items-center justify-center">
          <i className="fa-solid fa-circle-question text-primary text-sm" />
        </div>
        <h2 className="text-lg font-black text-text-primary font-heading">Frequently Asked Questions</h2>
      </div>
      <div className="space-y-2">
        {FAQS.map((faq, i) => (
          <div key={i} className="bg-white rounded-2xl border border-border overflow-hidden">
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left gap-3">
              <span className="text-sm font-semibold text-text-primary flex-1">{i + 1}. {faq.q}</span>
              <i className={`fa-solid fa-chevron-down text-primary text-xs flex-shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
            </button>
            {openIdx === i && (
              <div className="px-4 pb-4">
                <p className="text-sm text-text-secondary leading-relaxed">{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const STATIC_REVIEWS = [
  { _id:'s1', patientName:'Priya Sharma',  rating:5, comment:'Booked a slot in 2 minutes. The QR check-in was super smooth — no queue, no paper forms. Loved it!',  createdAt:'' },
  { _id:'s2', patientName:'Rahul Verma',   rating:5, comment:'Found a great cardiologist near me. Video call quality was excellent. FARISTAA saved me a 2-hour commute.',  createdAt:'' },
  { _id:'s3', patientName:'Anjali Patel',  rating:5, comment:'Returning patient discount was automatically applied. Saved ₹200 without even asking. Great service!', createdAt:'' },
  { _id:'s4', patientName:'Suresh Kumar',  rating:4, comment:'The token system is brilliant. I arrived exactly when my turn came. Doctor was punctual too.',         createdAt:'' },
  { _id:'s5', patientName:'Meera Nair',    rating:5, comment:'Medicine store chat is so helpful. Ordered my prescription medicines without a separate trip.',         createdAt:'' },
  { _id:'s6', patientName:'Deepak Joshi',  rating:5, comment:'Video consultation was crystal clear. Doctor explained everything patiently. Highly recommended!',      createdAt:'' },
];

// ── Auto-scrolling reviews carousel ─────────────────────────────────────
function ReviewsCarousel({ liveReviews }) {
  const scrollRef = useRef(null);
  const animRef   = useRef(null);
  const pausedRef = useRef(false);

  // Use real reviews if available, otherwise static fallback
  const reviews = liveReviews && liveReviews.length >= 3 ? liveReviews : STATIC_REVIEWS;
  // Duplicate for infinite scroll effect
  const doubled = [...reviews, ...reviews];

  // Auto-scroll — smooth pixel-by-pixel
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let pos = 0;
    const CARD_W  = 272; // card width + gap
    const TOTAL   = reviews.length * CARD_W;
    const SPEED   = 0.6; // px per frame

    const tick = () => {
      if (!pausedRef.current) {
        pos += SPEED;
        if (pos >= TOTAL) pos = 0;   // seamless loop
        el.scrollLeft = pos;
      }
      animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animRef.current);
  }, [reviews.length]);

  const pause  = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };

  return (
    <div className="mb-6">
      <div className="section-heading px-4 md:px-6 flex items-center justify-between">
        <span className="section-title">What Patients Say</span>
        {liveReviews && liveReviews.length > 0 && (
          <span className="text-[10px] text-success font-semibold flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-success inline-block animate-pulse" />
            Live reviews
          </span>
        )}
      </div>

      {/* Scroll container — hidden scrollbar, no touch-drag interference */}
      <div
        ref={scrollRef}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
        className="flex gap-3 px-4 md:px-6 pb-3 overflow-x-hidden"
        style={{ scrollBehavior: 'auto', cursor: 'default' }}
      >
        {doubled.map((r, i) => (
          <div
            key={`${r._id}-${i}`}
            className="flex-shrink-0 w-64 bg-white border border-border rounded-2xl p-4 shadow-sm"
            style={{ minWidth: 256 }}
          >
            {/* Stars */}
            <div className="flex items-center gap-1 mb-2">
              {[1,2,3,4,5].map(n => (
                <i key={n}
                  className={`fa-solid fa-star text-xs ${n <= r.rating ? 'text-amber-400' : 'text-slate-200'}`} />
              ))}
              <span className="text-[10px] text-text-muted ml-1">{r.rating}.0</span>
            </div>

            {/* Review text */}
            <p className="text-[12px] text-text-secondary leading-relaxed mb-3 line-clamp-3">
              "{r.comment}"
            </p>

            {/* Patient info */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary-light flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
                {(r.patientName || 'P')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-xs font-bold text-text-primary">{r.patientName || 'Patient'}</p>
                {r.doctorName && (
                  <p className="text-[10px] text-text-muted">via Dr. {r.doctorName}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user, updateUser, isLoggedIn, isHydrated } = useAuth();
  const settings = useAppSettings();
  const { unreadCount, notifications, markRead, deleteOne: deleteNotif, deleteAll: clearNotifs } = usePatientNotifications();
  const { socket } = useSocket();
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [showNotifs, setShowNotifs]     = useState(false);
  const [liveReviews, setLiveReviews]   = useState([]);
  const [legalModal, setLegalModal]     = useState(null); // 'terms'|'privacy'|'refu

  // Real-time: listen for new app reviews submitted by any user
  useEffect(() => {
    if (!socket) return;
    const handler = (review) => {
      setLiveReviews(prev => {
        const normalized = { _id: review._id, patientName: review.name, rating: review.rating, comment: review.comment, createdAt: review.createdAt };
        // Prepend, keep max 30
        return [normalized, ...prev.slice(0, 29)];
      });
    };
    socket.on('new_app_review', handler);
    return () => socket.off('new_app_review', handler);
  }, [socket]);
  useEffect(() => {
    getAppReviews(20).then(res => {
      if (res?.reviews?.length >= 3) {
        // Normalize shape to match what ReviewsCarousel expects
        setLiveReviews(res.reviews.map(r => ({
          _id: r._id,
          patientName: r.name || 'Anonymous',
          rating: r.rating,
          comment: r.comment,
          role: r.role,
          createdAt: r.createdAt,
        })));
      } else {
        // fallback to doctor reviews
        getLatestReviews(10).then(r2 => {
          if (r2?.reviews?.length) setLiveReviews(r2.reviews);
        }).catch(() => {});
      }
    }).catch(() => {
      getLatestReviews(10).then(res => {
        if (res?.reviews?.length) setLiveReviews(res.reviews);
      }).catch(() => {});
    });
  }, []);
  const [location, setLocation]         = useState(user?.location?.city || '');
  // Sync location from user data after hydration (user is null briefly on refresh)
  React.useEffect(() => {
    if (isHydrated && user?.location?.city && !location) {
      setLocation(user.location.city);
    }
  }, [isHydrated, user?.location?.city]); // eslint-disable-line
  const [searchQ, setSearchQ]           = useState('');
  const [allDoctors, setAllDoctors]     = useState([]);
  const [nearbyDoctors, setNearbyDoctors] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [locating, setLocating]         = useState(false);

  const fetchDoctors = useCallback(async (city='') => {
    setLoading(true);
    try {
      const params = { limit: 20 };
      if (city) params.location = city;
      const res = await searchDoctors(params);
      if (city) setNearbyDoctors(res.doctors || []);
      else setAllDoctors(res.doctors || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const handleLocate = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async pos => {
        try {
          const { latitude:lat, longitude:lng } = pos.coords;
          const res  = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
          const data = await res.json();
          const city = data.address?.city || data.address?.town || data.address?.village || 'Your Location';
          setLocation(city);
          updateUser({ location:{ city, area:'' } });
          toast.success(`Location: ${city}`);
          fetchDoctors(city);
        } catch { setLocation('Your Location'); }
        finally { setLocating(false); }
      },
      () => { toast.error('Could not get location'); setLocating(false); }
    );
  };



  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQ.trim()) params.set('q', searchQ);
    if (location.trim()) params.set('location', location);
    navigate(`/search?${params.toString()}`);
  };

  const displayDoctors = nearbyDoctors.length > 0 ? nearbyDoctors : allDoctors;

  return (
    <div className="min-h-screen bg-surface">
      {/* Header with notification bell */}
      <div className="relative">
        <Header showLogo onMenuClick={() => setSidebarOpen(true)}
          showNotif={isLoggedIn} notifCount={unreadCount}
          onNotifClick={() => { setShowNotifs(!showNotifs); if(!showNotifs) markRead(); }} />
        {showNotifs && (
          <NotificationsPanel notifications={notifications} unreadCount={unreadCount}
            onClose={() => setShowNotifs(false)} onMarkAllRead={markRead}
            onDeleteOne={deleteNotif} onDeleteAll={clearNotifs}
            onTap={(notif) => {
              setShowNotifs(false);
              if (notif.type === 'new_message' && notif.data?.conversationId) {
                const isStoreMsg = notif.data?.isStore || notif.title?.includes('Medicine Store');
                if (isStoreMsg) {
                  // Medicine store notification → extract doctorId from conversationId
                  const safeDoctorId = notif.data?.doctorId ||
                    (notif.data.conversationId.match(/store_([0-9a-f]{24})/i) || [])[1];
                  if (safeDoctorId) { navigate(`/medicine-chat/${safeDoctorId}`); return; }
                }
                // FIX: Use conversationId directly from notification data
                // This ensures we open the EXISTING conversation, not create a new one
                // conversationId format: sorted [patientId, doctorId] joined by _
                navigate(`/chat/${notif.data.conversationId}`);
              } else if (['checkin_confirmed','appointment_completed','new_booking','slot_delay'].includes(notif.type)) {
                navigate('/appointments');
              }
            }}
          />
        )}
      </div>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main content — responsive: single col mobile, wider on desktop */}
      <div className="pt-14 pb-20 md:pb-8">

        {/* ── Hero / Search Section ─────────────────────────────── */}
        <div style={{ background:'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)' }} className="px-4 pt-6 pb-8 md:px-8 lg:px-16">
          <div className="max-w-2xl mx-auto">
            {/* ── Unified Search Bar — live location LEFT + search RIGHT ── */}
            <form onSubmit={handleSearch} className="relative">
              <div className="flex items-center bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-white/20">

                {/* LEFT: Live location button */}
                <button type="button" onClick={handleLocate} disabled={locating}
                  className="flex items-center gap-2 px-3.5 py-3.5 border-r border-slate-200 flex-shrink-0 active:bg-slate-50 transition-colors"
                  title="Detect my location">
                  {locating
                    ? <i className="fa-solid fa-spinner animate-spin text-primary text-base" />
                    : <i className={`fa-solid fa-location-dot text-base ${location ? 'text-primary' : 'text-slate-400'}`} />}
                  <span className={`text-sm font-semibold hidden sm:block max-w-[100px] truncate ${location ? 'text-primary' : 'text-slate-400'}`}>
                    {location || 'Location'}
                  </span>
                </button>

                {/* RIGHT: Text search input */}
                <div className="flex items-center flex-1 px-3 py-2">
                  <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                    placeholder="Doctor, clinic, speciality, city..."
                    className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400 min-w-0" />
                  {searchQ && (
                    <button type="button" onClick={() => setSearchQ('')} className="mr-1 flex-shrink-0">
                      <i className="fa-solid fa-xmark text-slate-400 text-sm" />
                    </button>
                  )}
                  <button type="submit"
                    className="flex-shrink-0 w-9 h-9 bg-primary rounded-xl flex items-center justify-center ml-1 active:scale-95 transition-transform">
                    <i className="fa-solid fa-magnifying-glass text-white text-sm" />
                  </button>
                </div>
              </div>

              {/* Location pill — shown when location is set */}
              {location && (
                <div className="flex items-center gap-2 mt-2.5">
                  <div className="flex items-center gap-1.5 bg-white/20 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                    <i className="fa-solid fa-location-dot text-[10px]" />
                    {location}
                    <button type="button" onClick={() => setLocation('')} className="ml-1 opacity-70">
                      <i className="fa-solid fa-xmark text-[10px]" />
                    </button>
                  </div>
                  <span className="text-white/60 text-xs">Searching near this area</span>
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="max-w-5xl mx-auto">

          {/* ── Speciality chips ─────────────────────────────────── */}
          <div className="mb-5 mt-4">
            <div className="section-heading px-4 md:px-6">
              <span className="section-title">Find by Speciality</span>
              <button onClick={() => navigate('/search')} className="section-view-all">View All</button>
            </div>
            <div className="flex gap-3 px-4 md:px-6 overflow-x-auto scrollbar-hide pb-2">
              {DISEASES.map((d,i) => (
                <DiseaseChip key={d.label} icon={d.icon} label={d.label}
                  bg={DISEASE_COLORS[i%DISEASE_COLORS.length].bg}
                  color={DISEASE_COLORS[i%DISEASE_COLORS.length].color}
                  onClick={() => navigate(`/search?speciality=${encodeURIComponent(d.label)}`)} />
              ))}
            </div>
          </div>

          {/* ── Doctor by Disease ────────────────────────────────── */}
          <div className="mb-5">
            <div className="section-heading px-4 md:px-6">
              <span className="section-title">Doctor by Disease</span>
              <button onClick={() => navigate('/search')} className="section-view-all">View All</button>
            </div>
            <div className="flex gap-3 px-4 md:px-6 overflow-x-auto scrollbar-hide pb-2">
              {[
                { label:'Heart Disease',    icon:'fa-heart-crack',        bg:'#FEE2E2', color:'#DC2626', speciality:'Cardiologist' },
                { label:'Skin & Hair',      icon:'fa-face-smile',         bg:'#FEF3C7', color:'#D97706', speciality:'Dermatologist' },
                { label:'Eye Care',         icon:'fa-glasses',            bg:'#DBEAFE', color:'#2563EB', speciality:'Ophthalmologist' },
                { label:'Ortho & Spine',    icon:'fa-person-walking',     bg:'#F3E8FF', color:'#9333EA', speciality:'Orthopedic' },
                { label:'Dental & Teeth',   icon:'fa-teeth',              bg:'#ECFDF5', color:'#059669', speciality:'Dentist' },
                { label:'Child Care',       icon:'fa-child-reaching',     bg:'#FFF7ED', color:'#EA580C', speciality:'Pediatrician' },
                { label:"Women's Care",     icon:'fa-ribbon',             bg:'#FDF2F8', color:'#DB2777', speciality:'Gynecologist' },
                { label:'ENT Problems',     icon:'fa-head-side-cough',    bg:'#F0FDF4', color:'#16A34A', speciality:'ENT Specialist' },
              ].map(d => (
                <button key={d.label}
                  onClick={() => navigate(`/search?speciality=${encodeURIComponent(d.speciality)}`)}
                  className="flex-shrink-0 flex flex-col items-center gap-2 w-[80px] active:scale-95 transition-transform">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ background: d.bg }}>
                    <i className={`fa-solid ${d.icon} text-2xl`} style={{ color: d.color }} />
                  </div>
                  <span className="text-[10px] text-text-secondary font-semibold text-center leading-tight">{d.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Top Clinics ────────────────────────────────────── */}
          <div className="mb-5">
            <div className="section-heading px-4 md:px-6">
              <span className="section-title">
                {nearbyDoctors.length > 0 ? `Nearest Doctors (${location})` : 'Top Clinics'}
              </span>
              <button onClick={() => navigate('/search'+(location?`?location=${encodeURIComponent(location)}`:'' ))} className="section-view-all">View All</button>
            </div>
            {loading ? (
              <div className="flex gap-3 px-4 md:px-6 overflow-x-auto">
                {[1,2,3].map(i => <div key={i} className="flex-shrink-0 w-44 h-52 skeleton rounded-2xl" />)}
              </div>
            ) : displayDoctors.length === 0 ? (
              <p className="px-4 md:px-6 text-sm text-text-muted">No verified doctors yet</p>
            ) : (
              <div className="flex gap-3 px-4 md:px-6 overflow-x-auto scrollbar-hide pb-2">
                {displayDoctors.map(doc => <ClinicCard key={doc._id} doctor={doc} />)}
              </div>
            )}
          </div>

          {/* ── Checkup & Tests ──────────────────────────────────── */}
          <div className="mb-5">
            <div className="section-heading px-4 md:px-6">
              <span className="section-title">Checkup & Tests</span>
            </div>
            <div className="flex gap-2 px-4 md:px-6 overflow-x-auto scrollbar-hide pb-2">
              {CHECKUP_TYPES.map(c => (
                <CheckupChip key={c.label} icon={c.icon} label={c.label}
                  onClick={() => navigate(`/search?speciality=${encodeURIComponent(c.label)}`)} />
              ))}
            </div>
          </div>

          {/* ── Top Doctors grid (responsive) ────────────────────── */}
          <div className="mb-4">
            <div className="section-heading px-4 md:px-6">
              <span className="section-title">Top Doctors</span>
              <button onClick={() => navigate('/search')} className="section-view-all">View All</button>
            </div>
            {loading ? <InlineLoader text="Loading..." /> :
             displayDoctors.length === 0 ? (
              <div className="px-4 text-center py-8">
                <i className="fa-solid fa-user-doctor text-3xl text-text-muted mb-2 block" />
                <p className="text-sm text-text-muted">No verified doctors yet</p>
              </div>
            ) : (
              <div className="px-4 md:px-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                {displayDoctors.slice(0,6).map(doc => <DoctorCard key={doc._id} doctor={doc} />)}
              </div>
            )}
            {displayDoctors.length > 6 && (
              <div className="px-4 md:px-6 mt-3">
                <button onClick={() => navigate('/search')}
                  className="w-full py-3 border border-primary text-primary text-sm font-semibold rounded-btn">
                  View All {displayDoctors.length}+ Doctors
                </button>
              </div>
            )}
          </div>

          {/* ── Our Services ─────────────────────────────────────── */}
          <div className="px-4 md:px-6 mb-6 mt-2">
            <div className="section-heading mb-4 px-0">
              <span className="section-title">Our Services</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { icon:'fa-calendar-check',   title:'Online Booking',   desc:'Book slots instantly', color:'bg-primary-light', iconColor:'text-primary' },
                { icon:'fa-video',            title:'Video Consult',     desc:'Consult from home', color:'bg-blue-50', iconColor:'text-info' },
                { icon:'fa-qrcode',           title:'QR Check-in',       desc:'Skip paperwork', color:'bg-purple-50', iconColor:'text-purple-600' },
                { icon:'fa-pills',            title:'Medicine Store',     desc:'Chat with pharmacy', color:'bg-green-50', iconColor:'text-success' },
                { icon:'fa-star',             title:'Verified Reviews',   desc:'Trusted feedback', color:'bg-amber-50', iconColor:'text-amber-500' },
                { icon:'fa-shield-halved',    title:'Secure Payments',   desc:'Encrypted checkout', color:'bg-red-50', iconColor:'text-danger' },
              ].map(s => (
                <div key={s.title} className={`${s.color} rounded-2xl p-3.5`}>
                  <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center mb-2 shadow-sm">
                    <i className={`fa-solid ${s.icon} ${s.iconColor} text-sm`} />
                  </div>
                  <p className="text-xs font-bold text-text-primary">{s.title}</p>
                  <p className="text-[10px] text-text-secondary mt-0.5">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── About Us ─────────────────────────────────────────── */}
          <AboutUs />

          {/* ── Why Choose FARISTAA ───────────────────────────────── */}
          <div className="px-4 md:px-6 mb-6">
            <div className="section-heading mb-4 px-0">
              <span className="section-title">Why Choose FARISTAA?</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { icon:'fa-shield-halved',   color:'text-primary',    bg:'bg-primary-light',  title:'100% Verified Doctors',      desc:'Every doctor is manually verified by our team before going live on the platform.' },
                { icon:'fa-lock',            color:'text-purple-600', bg:'bg-purple-50',      title:'Secure & Private',           desc:'Your health data is encrypted. We never share your personal information with third parties.' },
                { icon:'fa-rotate-left',     color:'text-green-600',  bg:'bg-green-50',       title:'Easy Refunds',               desc:'Cancel before your appointment and get a refund within 5-7 business days, no questions asked.' },
                { icon:'fa-headset',         color:'text-amber-600',  bg:'bg-amber-50',       title:'24/7 Support',               desc:'WhatsApp, call, or email — our support team is always available to help you.' },
                { icon:'fa-clock',           color:'text-blue-600',   bg:'bg-blue-50',        title:'No Waiting Room',           desc:"Get your token number before you arrive. Walk in exactly when it's your turn." },
                { icon:'fa-video',           color:'text-teal-600',   bg:'bg-teal-50',        title:'Video Consultations',        desc:'Consult top doctors from the comfort of your home via secure HD video calls.' },
              ].map(f => (
                <div key={f.title} className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-border shadow-sm">
                  <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center flex-shrink-0`}>
                    <i className={`fa-solid ${f.icon} ${f.color} text-base`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-text-primary">{f.title}</p>
                    <p className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Patient Reviews — real-time from DB, auto-scroll ── */}
          <div id="patient-reviews-section">
            <ReviewsCarousel liveReviews={liveReviews} />
          </div>

          {/* ── Download App CTA ──────────────────────────────────── */}
          <div className="mx-4 md:mx-6 mb-6 p-5 bg-white border border-border rounded-2xl flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 text-center sm:text-left">
              <p className="text-base font-bold text-text-primary font-heading">Book appointments on the go</p>
              <p className="text-xs text-text-muted mt-1">FARISTAA works perfectly on any browser — no app download needed.</p>
              <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                <span className="flex items-center gap-1.5 text-xs bg-primary-light text-primary font-semibold px-3 py-1.5 rounded-full">
                  <i className="fa-solid fa-bolt text-[10px]" />Instant Booking
                </span>
                <span className="flex items-center gap-1.5 text-xs bg-green-50 text-green-700 font-semibold px-3 py-1.5 rounded-full">
                  <i className="fa-solid fa-bell text-[10px]" />Real-time Alerts
                </span>
                <span className="flex items-center gap-1.5 text-xs bg-purple-50 text-purple-700 font-semibold px-3 py-1.5 rounded-full">
                  <i className="fa-solid fa-qrcode text-[10px]" />QR Check-in
                </span>
              </div>
            </div>
            <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30 flex-shrink-0">
              <i className="fa-solid fa-house-medical text-white text-3xl" />
            </div>
          </div>

          {/* Doctor CTA removed */}

          {/* ── How it works ─────────────────────────────────────── */}
          <div className="mx-4 md:mx-6 mb-6 p-5 bg-gradient-to-br from-primary to-teal-600 rounded-2xl text-white">
            <h3 className="font-bold text-lg font-heading mb-4 text-center">How FARISTAA Works</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { step:'1', icon:'fa-magnifying-glass', title:'Search',     desc:'Find verified doctors' },
                { step:'2', icon:'fa-calendar-check',   title:'Book Slot',  desc:'Choose time & pay' },
                { step:'3', icon:'fa-qrcode',           title:'Get Token',  desc:'Receive QR code' },
                { step:'4', icon:'fa-hospital-user',    title:'Visit',      desc:'Clinic or video' },
              ].map(s => (
                <div key={s.step} className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-2 text-sm font-bold">{s.step}</div>
                  <p className="font-semibold text-sm">{s.title}</p>
                  <p className="text-xs text-white/80 mt-0.5">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── FAQ Section ──────────────────────────────────────────── */}
          <FAQSection />

          {/* ── Footer ───────────────────────────────────────────── */}
          <footer className="border-t border-border pt-8 pb-4" style={{width:'100vw',marginLeft:'calc(50% - 50vw)',paddingLeft:'1rem',paddingRight:'1rem',boxSizing:'border-box'}}>
            {/* Top row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              {/* Brand */}
              <div className="md:col-span-1">
                <img src="/logo-header.png" alt="Faristaa" className="h-8 w-auto mb-2" onError={e=>e.target.style.display='none'} />
                <p className="text-xs text-text-muted mt-1 leading-relaxed">Your trusted healthcare partner — making quality care accessible across India.</p>
                <button onClick={() => setLegalModal('about')}
                  className="mt-2 text-xs font-semibold text-primary hover:underline text-left">
                  Learn more about us →
                </button>
                {/* Social icons — real links from admin settings or hardcoded defaults */}
                <div className="flex gap-2 mt-3">
                  {[
                    { icon:'fa-facebook',  href: settings.facebookUrl        || 'https://www.facebook.com/share/1E1PyeaFPu/',       label:'Facebook'  },
                    { icon:'fa-instagram', href: settings.instagramUrl       || 'https://www.instagram.com/faristaa24?igsh=bjh5ZnJmYndzd2hy', label:'Instagram' },
                    { icon:'fa-whatsapp',  href: settings.whatsappChannelUrl || 'https://youtube.com/@faristaa-h8k?si=culyiEwxvDr3i30t', label:'WhatsApp'  },
                    { icon:'fa-youtube',   href: settings.youtubeUrl         || 'https://www.youtube.com/@faristaa-h8k',            label:'YouTube'   },
                  ].map(s => (
                    <a key={s.icon} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                      className="w-8 h-8 rounded-full bg-primary-light flex items-center justify-center hover:bg-primary transition-colors group">
                      <i className={`fa-brands ${s.icon} text-primary text-sm group-hover:text-white`} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Support */}
              <div>
                <p className="text-sm font-bold text-text-primary mb-2">Support</p>
                {settings.whatsappSupportNumber && (
                  <a href={`https://wa.me/91${settings.whatsappSupportNumber}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-success py-1 font-medium">
                    <i className="fa-brands fa-whatsapp" /> WhatsApp Support
                  </a>
                )}
                {settings.callSupportNumber && (
                  <a href={`tel:${settings.callSupportNumber}`}
                    className="flex items-center gap-1.5 text-xs text-text-secondary py-1 hover:text-primary">
                    <i className="fa-solid fa-phone text-primary text-[10px]" /> Call Support
                  </a>
                )}
                {settings.contactEmail && (
                  <a href={`mailto:${settings.contactEmail}`}
                    className="flex items-center gap-1.5 text-xs text-text-secondary py-1 hover:text-primary truncate">
                    <i className="fa-solid fa-envelope text-primary text-[10px]" /> {settings.contactEmail}
                  </a>
                )}
                {settings.faqUrl && (
                  <a href={settings.faqUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-text-secondary py-1 hover:text-primary">
                    <i className="fa-solid fa-circle-question text-primary text-[10px]" /> FAQ
                  </a>
                )}
                {(settings.patientTutorialUrl || settings.tutorialsUrl) && (
                  <a href={settings.patientTutorialUrl || settings.tutorialsUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-text-secondary py-1 hover:text-primary">
                    <i className="fa-solid fa-play-circle text-accent text-[10px]" /> Patient Tutorial
                  </a>
                )}
                {settings.doctorTutorialUrl && (
                  <a href={settings.doctorTutorialUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-text-secondary py-1 hover:text-primary">
                    <i className="fa-solid fa-play-circle text-purple-500 text-[10px]" /> Doctor Tutorial
                  </a>
                )}
              </div>

              {/* Legal */}
              <div>
                <p className="text-sm font-bold text-text-primary mb-2">Legal</p>
                {[
                  { label:'Terms & Conditions', key:'terms' },
                  { label:'Privacy Policy',     key:'privacy' },
                  { label:'Refund Policy',      key:'refund' },
                  { label:'Cookie Policy',      key:'cookie' },
                  { label:'Discount Offer Terms', key:'discount_terms' },
                  { label:'About Us',           key:'about' },
                ].map(l => (
                  <button key={l.key} onClick={() => setLegalModal(l.key)}
                    className="block text-xs text-text-secondary py-1 hover:text-primary text-left w-full">{l.label}</button>
                ))}
                <p className="text-sm font-bold text-text-primary mb-2 mt-3">For Doctors</p>
                <button onClick={() => navigate('/doctor/login')}
                  className="block text-xs text-text-secondary py-1 hover:text-primary text-left w-full">Join as Doctor</button>
              </div>
            </div>

            {/* Bottom — copyright only, no duplicate links */}
            <div className="border-t border-border pt-4">
            <p className="text-center text-xs text-text-muted mt-3">
              © {new Date().getFullYear()} Faristaa (FARISTAA HEALTHTECH PRIVATE LIMITED). All rights reserved.
            </p>
            <p className="text-center text-xs text-text-muted mt-1">
              <span className="font-semibold text-text-primary">FARISTAA HEALTHTECH PRIVATE LIMITED</span>
              <br />
              Crafted with <i className="fa-solid fa-code text-primary text-[11px] mx-0.5" /> by{' '}
              <a
                href="https://professionalportfolio01.netlify.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-primary hover:underline inline-flex items-center gap-1 transition-colors"
                style={{ textDecoration:'none' }}
              >
                Raj Singh
                <i className="fa-solid fa-arrow-up-right-from-square text-[9px]" />
              </a>
            </p>
            </div>
          </footer>
        </div>
      </div>

      {/* ── Legal Policy Modals ── */}
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}

      <BottomNav notifCount={unreadCount} unreadNotif={unreadCount} onNotifClick={() => { setShowNotifs(p => !p); if (!showNotifs) markRead(); }} />
    </div>
  );
}
